import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Bot, Clock, Target, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { EXERCISES } from "@/data/exercises";
import { parseWorkoutPlanFromMessage, findMatchingExercise } from "@/utils/workoutImporter";
import { toast } from "sonner";
import { Calendar } from "lucide-react";

interface ArnoldWorkout {
  id: string;
  title: string;
  content: string;
  timestamp: string;
  exercises: Array<{
    name: string;
    sets?: number;
    reps?: number;
    weight?: string;
    duration?: string;
    notes?: string;
  }>;
}

interface ArnoldWorkoutProgram {
  id: string;
  title: string;
  content: string;
  timestamp: string;
  days: Array<{
    day: string;
    workout_name: string;
    exercises: Array<{
      name: string;
      sets?: number;
      reps?: number;
      weight?: string;
    }>;
  }>;
}

type ArnoldItem = ArnoldWorkout | ArnoldWorkoutProgram;

interface ArnoldWorkoutImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onWorkoutImport: (workout: ArnoldWorkout) => void;
}

export const ArnoldWorkoutImportModal = ({ isOpen, onClose, onWorkoutImport }: ArnoldWorkoutImportModalProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [arnoldWorkouts, setArnoldWorkouts] = useState<ArnoldWorkout[]>([]);
  const [arnoldPrograms, setArnoldPrograms] = useState<ArnoldWorkoutProgram[]>([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [expandedProgramId, setExpandedProgramId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && user) {
      loadArnoldWorkouts();
    }
  }, [isOpen, user]);

  const loadArnoldWorkouts = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      console.log('Loading Arnold workouts for user:', user.id);
      
      // Get all conversations and their messages from Arnold
      const { data: conversations, error: convError } = await supabase
        .from('conversations')
        .select(`
          id,
          messages!inner(*)
        `)
        .eq('user_id', user.id)
        .eq('messages.sender', 'ai')
        .order('updated_at', { ascending: false });

      if (convError) {
        console.error('Error fetching conversations:', convError);
        throw convError;
      }

      console.log('Fetched conversations:', conversations?.length || 0);

      const workouts: ArnoldWorkout[] = [];
      const programs: ArnoldWorkoutProgram[] = [];

      conversations?.forEach(conv => {
        conv.messages?.forEach((message: any) => {
          const parsedWorkout = parseWorkoutFromMessage(message);
          if (parsedWorkout) {
            workouts.push({
              id: `message_${message.id}`,
              ...parsedWorkout,
              timestamp: message.created_at,
            });
          }
          
          // Also check for workout programs
          const parsedProgram = parseWorkoutProgramFromMessage(message);
          if (parsedProgram) {
            programs.push({
              id: `program_${message.id}`,
              ...parsedProgram,
              timestamp: message.created_at,
            });
          }
        });
      });

      // NEW: Also load saved templates (Start later) so they appear immediately
      const { data: templates, error: tmplErr } = await supabase
        .from('workout_templates')
        .select('id, title, exercises, created_at, original_message_id, source')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (tmplErr) console.warn('Error loading workout templates:', tmplErr);

      (templates || []).forEach((t: any) => {
        // Build a minimal ArnoldWorkout record from template
        const exs = Array.isArray(t.exercises) ? t.exercises : [];
        if (exs.length === 0) return;
        workouts.push({
          id: `template_${t.id}`,
          title: createShortTitle(t.title || "AI Workout"),
          content: JSON.stringify({ type: 'single_workout', exercises: exs }),
          exercises: exs.map((ex: any) => ({
            name: String(ex.exercise_name || ex.name || '').trim(),
            sets: ex.sets,
            reps: ex.reps,
            weight: ex.weight_kg ? `${ex.weight_kg} kg` : undefined,
          })),
          timestamp: t.created_at,
        });
      });

      // Sort by most recent first and deduplicate by title
      const uniqueWorkouts = workouts
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .filter((workout, index, arr) => 
          arr.findIndex(other => other.title.toLowerCase() === workout.title.toLowerCase() && other.timestamp === workout.timestamp) === index
        )
        .slice(0, 20);
      
      const uniquePrograms = programs
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .filter((program, index, arr) => 
          arr.findIndex(other => other.title.toLowerCase() === program.title.toLowerCase()) === index
        )
        .slice(0, 10);
      
      setArnoldWorkouts(uniqueWorkouts);
      setArnoldPrograms(uniquePrograms);
    } catch (error) {
      console.error('Error loading Arnold workouts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to create short workout titles (2-4 words max)
  const createShortTitle = (originalTitle: string): string => {
    const title = originalTitle.toLowerCase();
    
    // Map common workout parts to shorter versions
    const bodyPartMap: { [key: string]: string } = {
      'bröst': 'Chest',
      'chest': 'Chest',
      'rygg': 'Back', 
      'back': 'Back',
      'ben': 'Legs',
      'legs': 'Legs',
      'axlar': 'Shoulders',
      'shoulders': 'Shoulders',
      'armar': 'Arms',
      'arms': 'Arms',
      'biceps': 'Biceps',
      'triceps': 'Triceps',
      'core': 'Core',
      'abs': 'Abs'
    };
    
    const workoutTypeMap: { [key: string]: string } = {
      'workout': 'Workout',
      'träning': 'Workout', 
      'pass': 'Session',
      'routine': 'Routine',
      'program': 'Program'
    };
    
    // Extract body part and workout type
    let bodyPart = '';
    let workoutType = 'Workout';
    
    for (const [key, value] of Object.entries(bodyPartMap)) {
      if (title.includes(key)) {
        bodyPart = value;
        break;
      }
    }
    
    for (const [key, value] of Object.entries(workoutTypeMap)) {
      if (title.includes(key)) {
        workoutType = value;
        break;
      }
    }
    
    // Create short title
    if (bodyPart) {
      return `${bodyPart} ${workoutType}`;
    }
    
    // Fallback: try to extract meaningful words and limit to 2-3 words
    const words = originalTitle
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 2)
      .slice(0, 3);
      
    return words.length > 0 ? words.join(' ') : 'AI Workout';
  };

  // Helper: pick highest number from text (supports ranges like 8–10, 8-10, decimals, commas)
  // ALWAYS returns the MAXIMUM value from ranges (e.g., "10-12" returns 12, "90-95kg" returns 95)
  const getMaxNumberFromText = (text: string): number | null => {
    const matches = text.match(/(\d+(?:[.,]\d+)?)/g);
    if (!matches) return null;
    const nums = matches.map(n => parseFloat(n.replace(',', '.'))).filter(n => !isNaN(n));
    if (nums.length === 0) return null;
    return Math.max(...nums);
  };

  // Function to find matching exercises from the database
  const findMatchingExercise = (exerciseName: string) => {
    const cleanName = exerciseName.trim().toLowerCase();
    
    // First try exact match
    const exactMatch = EXERCISES.find(ex => ex.name.toLowerCase() === cleanName);
    if (exactMatch) return exactMatch;
    
    // Try partial matches - exercise name contains the searched term or vice versa
    const partialMatch = EXERCISES.find(ex => {
      const exName = ex.name.toLowerCase();
      return exName.includes(cleanName) || cleanName.includes(exName);
    });
    if (partialMatch) return partialMatch;
    
    // Try matching with common variations and synonyms
    const synonymMap: { [key: string]: string[] } = {
      'press': ['push', 'pressing'],
      'curl': ['curls'],
      'squat': ['squats'],
      'deadlift': ['dead lift', 'deadlifts'],
      'pull up': ['pullup', 'pull-up', 'pullups'],
      'push up': ['pushup', 'push-up', 'pushups'],
      'bench press': ['bench', 'benchpress'],
      'shoulder press': ['overhead press', 'military press'],
      'lat pulldown': ['pulldown', 'lat pull down'],
      'leg press': ['legpress']
    };
    
    // Check if the exercise name matches any synonyms
    for (const exercise of EXERCISES) {
      const exName = exercise.name.toLowerCase();
      
      // Check direct synonyms
      for (const [key, synonyms] of Object.entries(synonymMap)) {
        if ((exName.includes(key) && synonyms.some(syn => cleanName.includes(syn))) ||
            (cleanName.includes(key) && synonyms.some(syn => exName.includes(syn)))) {
          return exercise;
        }
      }
    }
    
    return null;
  };

  const parseWorkoutProgramFromMessage = (message: any): Omit<ArnoldWorkoutProgram, 'id' | 'timestamp'> | null => {
    const content = message.content;
    
    try {
      const plan = parseWorkoutPlanFromMessage(content as string);
      if (plan && plan.type === 'weekly_plan') {
        // Map the exercises to match the ArnoldWorkoutProgram interface
        const mappedDays = plan.days.map(day => ({
          day: day.day,
          workout_name: day.workout_name,
          exercises: day.exercises.map(ex => ({
            name: ex.exercise_name,
            sets: ex.sets,
            reps: ex.reps,
            weight: ex.weight_kg ? `${ex.weight_kg} kg` : undefined
          }))
        }));
        
        return {
          title: createShortTitle("Workout Program"),
          content,
          days: mappedDays,
        };
      }
    } catch (e) {
      console.warn('Failed to parse workout program:', e);
    }
    
    return null;
  };

  const parseWorkoutFromMessage = (message: any): Omit<ArnoldWorkout, 'id' | 'timestamp'> | null => {
    const content = message.content;
    const contentLower = content.toLowerCase();

    // 1) Prefer structured JSON plans first (saved inside the AI message)
    try {
      const plan = parseWorkoutPlanFromMessage(content as string);
      if (plan) {
        // Helper to map exercises while enforcing Strive exercise library and choosing max values
        const mapExercises = (exs: any[]) => exs
          .map((ex) => {
            const matched = findMatchingExercise(String(ex.exercise_name || ''));
            if (!matched) return null;
            const setsVal = typeof ex.sets === 'number' ? ex.sets : getMaxNumberFromText(String(ex.sets ?? '')) || 3;
            const repsVal = typeof ex.reps === 'number' ? ex.reps : getMaxNumberFromText(String(ex.reps ?? '')) || 10;
            const w = ex.weight_kg ?? ex.weight ?? ex.weightKg;
            const weightNum = typeof w === 'number' ? w : (w !== undefined ? getMaxNumberFromText(String(w)) : null);
            return {
              name: matched.name,
              sets: Math.round(setsVal),
              reps: Math.round(repsVal),
              weight: weightNum !== null && weightNum !== undefined ? `${weightNum} kg` : undefined,
            };
          })
          .filter(Boolean) as ArnoldWorkout['exercises'];

        if ((plan as any).type === 'single_workout' && Array.isArray((plan as any).exercises)) {
          const exercises = mapExercises((plan as any).exercises);
          if (exercises.length > 0) {
            return {
              title: createShortTitle("AI Workout"),
              content,
              exercises,
            };
          }
        } else if ((plan as any).type === 'weekly_plan' && Array.isArray((plan as any).days)) {
          const firstDay = (plan as any).days.find((d: any) => Array.isArray(d.exercises) && d.exercises.length > 0);
          if (firstDay) {
            const exercises = mapExercises(firstDay.exercises);
            if (exercises.length > 0) {
              return {
                title: createShortTitle(firstDay.workout_name || firstDay.day || 'Weekly Plan'),
                content,
                exercises,
              };
            }
          }
        }
      }
    } catch (e) {
      console.warn('Failed to parse structured workout JSON from message:', e);
    }

    // 2) Fallback to markdown/text parsing
    // Check if the message contains workout-related keywords (expanded for Swedish)
    const workoutKeywords = [
      'workout', 'exercise', 'training', 'sets', 'reps', 'routine',
      'squat', 'deadlift', 'bench press', 'push-up', 'pull-up',
      'bicep', 'tricep', 'shoulder', 'chest', 'back', 'legs', 'gym',
      'marklyft', 'rodd', 'träna', 'övning', 'övningar',
      'bröst', 'rygg', 'axlar', 'ben', 'armar', 'muskler',
      'incline', 'dumbbell', 'press', 'flyes', 'dips', 'crossovers', 'curl',
      'vikt', 'vikter', 'kg', 'hantel', 'hantelpress'
    ];

    const hasWorkoutKeywords = workoutKeywords.some(keyword => contentLower.includes(keyword));

    if (!hasWorkoutKeywords) {
      console.log('No workout keywords found in message:', content.substring(0, 100));
      return null;
    }

    // Extract exercises using improved patterns for Arnold's format
    const lines = content.split('\n');
    
    let currentTitle = "AI Workout Plan";
    
    // Look for workout title in headers or bold text (improved Swedish detection)
    for (const line of lines) {
      if (line.includes('#') || line.includes('**')) {
        const cleanLine = line.replace(/[#*]/g, '').trim();
        if (cleanLine && (
          cleanLine.toLowerCase().includes('workout') || 
          cleanLine.toLowerCase().includes('routine') || 
          cleanLine.toLowerCase().includes('plan') || 
          cleanLine.toLowerCase().includes('pass') ||
          cleanLine.toLowerCase().includes('träning') ||
          cleanLine.toLowerCase().includes('bröst') ||
          cleanLine.toLowerCase().includes('rygg') ||
          cleanLine.toLowerCase().includes('ben') ||
          cleanLine.toLowerCase().includes('axlar')
        )) {
          currentTitle = cleanLine;
          break;
        }
      }
    }

    // Parse exercises with sets/reps that span multiple lines
    let currentExercise: string | null = null;
    let sets: number | null = null;
    let reps: number | null = null;
    let weight: string | null = null;
    const validExercises: any[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines and headers
      if (!line || line.startsWith('#')) continue;
      
      // Pattern 1: Numbered exercises with bold names: "1. **Exercise Name**"
      const numberedExerciseMatch = line.match(/^(\d+)\.\s*\*\*(.+?)\*\*/);
      if (numberedExerciseMatch) {
        // Save previous exercise if we have one and it's valid
        if (currentExercise) {
          const matchedExercise = findMatchingExercise(currentExercise);
          if (matchedExercise) {
            validExercises.push({
              name: matchedExercise.name,
              sets: sets || 3,
              reps: reps || 10,
              weight: weight || undefined,
            });
          }
        }
        
        currentExercise = numberedExerciseMatch[2].trim();
        sets = null;
        reps = null;
        weight = null;
        continue;
      }
      
      // Pattern 2: Bold exercise names: "**Exercise Name**"
      const boldExerciseMatch = line.match(/^\*\*(.+?)\*\*$/);
      if (boldExerciseMatch && !line.toLowerCase().includes('sets') && !line.toLowerCase().includes('reps')) {
        // Save previous exercise if we have one and it's valid
        if (currentExercise) {
          const matchedExercise = findMatchingExercise(currentExercise);
          if (matchedExercise) {
            validExercises.push({
              name: matchedExercise.name,
              sets: sets || 3,
              reps: reps || 10,
              weight: weight || undefined,
            });
          }
        }
        
        currentExercise = boldExerciseMatch[1].trim();
        sets = null;
        reps = null;
        weight = null;
        continue;
      }
      
      // Pattern 3: Sets line: "- **Sets:** 3" or "**Sets:** 3" (choose highest if range)
      const setsMatch = line.match(/\*\*Sets?:\*\*/i);
      if (setsMatch && currentExercise) {
        const max = getMaxNumberFromText(line);
        if (max !== null) sets = Math.round(max);
        continue;
      }
      
      // Pattern 4: Reps line: "- **Reps:** 8-10" or "**Reps:** 8-10" (choose highest if range)
      const repsMatch = line.match(/\*\*Reps?:\*\*/i);
      if (repsMatch && currentExercise) {
        const max = getMaxNumberFromText(line);
        if (max !== null) reps = Math.round(max);
        continue;
      }

      // Pattern 4b: Weight line (Vikt/Weight/Extra vikt) - choose highest if range
      const weightLineMatch = line.match(/^(?:-\s*)?\*\*(?:Vikt|Weight|Extra\s+vikt):\*\*\s*([^\n]+)/i);
      if (weightLineMatch && currentExercise) {
        const max = getMaxNumberFromText(weightLineMatch[1]);
        if (max !== null) weight = `${max} kg`;
        continue;
      }

      // Pattern: Swedish format "#### 1. Exercise Name"
      const swedishHeaderMatch = line.match(/^####\s*(\d+)\.\s*(.+)$/);
      if (swedishHeaderMatch) {
        // Save previous exercise if we have one and it's valid
        if (currentExercise) {
          const matchedExercise = findMatchingExercise(currentExercise);
          if (matchedExercise) {
            validExercises.push({
              name: matchedExercise.name,
              sets: sets || 3,
              reps: reps || 10,
              weight: weight || undefined,
            });
          }
        }
        
        currentExercise = swedishHeaderMatch[2].trim();
        sets = null;
        reps = null;
        weight = null;
        continue;
      }
      
      // Pattern: Multi-line Swedish format with "-" bullets for sets/reps
      if (line.startsWith('- **Sets:**') || line.startsWith('- **Reps:**')) {
        const swedishSetsMatch = line.match(/- \*\*Sets?:\*\*/i);
        if (swedishSetsMatch && currentExercise) {
          const max = getMaxNumberFromText(line);
          if (max !== null) sets = Math.round(max);
          continue;
        }
        
        const swedishRepsMatch = line.match(/- \*\*Reps?:\*\*/i);
        if (swedishRepsMatch && currentExercise) {
          const max = getMaxNumberFromText(line);
          if (max !== null) reps = Math.round(max);
          continue;
        }
      }
      
      // Pattern 5: Inline format: "Exercise: 3-4 sets x 10-12 reps @ 90-95kg"
      const inlineMatch = line.match(/^[-•*]?\s*(.+?):\s*(\d+)(?:[-–]\s*(\d+))?\s*sets?\s*[x×]\s*(\d+)(?:[-–]\s*(\d+))?\s*reps?(.*)$/i);
      if (inlineMatch) {
        const exerciseName = inlineMatch[1].trim();
        const matchedExercise = findMatchingExercise(exerciseName);
        if (matchedExercise) {
          const setsA = parseInt(inlineMatch[2]);
          const setsB = inlineMatch[3] ? parseInt(inlineMatch[3]) : setsA;
          const repsA = parseInt(inlineMatch[4]);
          const repsB = inlineMatch[5] ? parseInt(inlineMatch[5]) : repsA;
          const setsMax = Math.max(setsA, setsB);
          const repsMax = Math.max(repsA, repsB);

          // Extract weight from trailing text if present
          let inlineWeight: string | undefined = undefined;
          const trailing = inlineMatch[6] || '';
          const kgNumbers = trailing.match(/(\d+(?:[.,]\d+)?)\s*kg/gi);
          if (kgNumbers && kgNumbers.length > 0) {
            const nums = kgNumbers
              .map(s => parseFloat((s.match(/\d+(?:[.,]\d+)?/) || ['0'])[0].replace(',', '.')))
              .filter(n => !isNaN(n));
            if (nums.length > 0) inlineWeight = `${Math.max(...nums)} kg`;
          }

          validExercises.push({
            name: matchedExercise.name,
            sets: setsMax,
            reps: repsMax,
            weight: inlineWeight,
          });
        }
        continue;
      }
      
      // Pattern 6: Simple bullet point exercises - only add if they match our database
      const bulletMatch = line.match(/^[-•*]\s*([A-Za-zÀ-ÿ][A-Za-zÀ-ÿ\s\-']{2,40})$/);
      if (bulletMatch && !bulletMatch[1].toLowerCase().includes('sets') && !bulletMatch[1].toLowerCase().includes('reps')) {
        const exerciseName = bulletMatch[1].trim();
        const matchedExercise = findMatchingExercise(exerciseName);
        if (matchedExercise && exerciseName.length > 2 && exerciseName.length < 60) {
          validExercises.push({
            name: matchedExercise.name,
            sets: 3,
            reps: 10,
          });
        }
      }
    }
    
    // Add the last exercise if we have one and it's valid
    if (currentExercise) {
      const matchedExercise = findMatchingExercise(currentExercise);
      if (matchedExercise) {
        validExercises.push({
          name: matchedExercise.name,
          sets: sets || 3,
          reps: reps || 10,
          weight: weight || undefined,
        });
      }
    }

    // Deduplicate exercises by name
    const uniqueExercises = validExercises
      .filter((ex, index, arr) => 
        arr.findIndex(other => other.name === ex.name) === index
      );

    console.log('Raw lines from message:', lines.slice(0, 20));
    console.log('Valid exercises found from Arnold:', uniqueExercises);
    console.log('Current title found:', currentTitle);

    // Only return if we found some valid exercises that match our database
    if (uniqueExercises.length > 0) {
      return {
        title: createShortTitle(currentTitle),
        content: message.content,
        exercises: uniqueExercises,
      };
    }

    console.log('No valid exercises found for message content');
    return null;
  };

  const handleImportProgram = async (program: ArnoldWorkoutProgram) => {
    if (!user) return;
    
    setImporting(true);
    try {
      // Create the workout program
      const { data: createdProgram, error: programError } = await supabase
        .from('workout_programs')
        .insert({
          user_id: user.id,
          title: program.title,
          description: t('components.arnoldWorkoutImport.importDescription', { date: new Date(program.timestamp).toLocaleDateString() }),
          program_type: 'strength',
          duration_weeks: Math.ceil(program.days.length / 7),
          created_from_chat: true,
          status: 'draft',
        })
        .select()
        .single();

      if (programError) throw programError;

      // Create days
      for (let i = 0; i < program.days.length; i++) {
        const day = program.days[i];
        const { data: createdDay, error: dayError } = await supabase
          .from('workout_program_days')
          .insert({
            program_id: createdProgram.id,
            day_number: i + 1,
            day_name: day.day,
            workout_title: day.workout_name,
            workout_description: null,
            estimated_duration_minutes: day.exercises.length * 5,
            difficulty_level: 'medium',
          })
          .select()
          .single();

        if (dayError) throw dayError;

        // Create exercises for this day
        for (let j = 0; j < day.exercises.length; j++) {
          const exercise = day.exercises[j];
          const matchedExercise = findMatchingExercise(exercise.name);
          
          const weightNum = typeof exercise.weight === 'string' 
            ? parseFloat(exercise.weight) || null 
            : null;
          
          await supabase.from('workout_program_exercises').insert({
            program_day_id: createdDay.id,
            exercise_name: matchedExercise?.name || exercise.name,
            muscle_groups: matchedExercise?.muscle_groups || [],
            sets: exercise.sets || 3,
            reps_min: exercise.reps || 10,
            reps_max: exercise.reps || 10,
            weight_kg: weightNum,
            rest_seconds: 90,
            notes: null,
            exercise_order: j + 1,
          });
        }
      }

      toast.success(t('components.arnoldWorkoutImport.importedToast'));
      onClose();
    } catch (error) {
      console.error('Error importing workout program:', error);
      toast.error(t('components.arnoldWorkoutImport.importErrorToast'));
    } finally {
      setImporting(false);
    }
  };

  const handleSelectWorkoutFromProgram = (program: ArnoldWorkoutProgram, dayIndex: number) => {
    const day = program.days[dayIndex];
    
    // Convert program day to workout format
    const workout: ArnoldWorkout = {
      id: `${program.id}_day_${dayIndex}`,
      title: day.workout_name,
      content: program.content,
      timestamp: program.timestamp,
      exercises: day.exercises.map(ex => ({
        name: ex.name,
        sets: ex.sets,
        reps: ex.reps,
        weight: ex.weight,
      })),
    };
    
    onWorkoutImport(workout);
    onClose();
  };

  const handleSelectWorkout = (workout: ArnoldWorkout) => {
    onWorkoutImport(workout);
    onClose();
  };

  const handleChatWithArnold = () => {
    navigate('/chat');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            {t('components.arnoldWorkoutImport.title')}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[70vh]">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('components.arnoldWorkoutImport.loading')}
            </div>
          ) : arnoldPrograms.length === 0 && arnoldWorkouts.length === 0 ? (
            <div className="text-center py-12 space-y-4">
              <Bot className="h-12 w-12 text-muted-foreground mx-auto" />
              <div>
                <p className="text-muted-foreground mb-2">
                  {t('components.arnoldWorkoutImport.emptyMessage')}
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('components.arnoldWorkoutImport.emptyHint')}
                </p>
                <Button onClick={handleChatWithArnold} className="bg-gradient-primary text-black">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  {t('components.arnoldWorkoutImport.chatWithCoach')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6 pr-4">
              {/* Workout Programs Section */}
              {arnoldPrograms.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase">{t('components.arnoldWorkoutImport.workoutPrograms')}</h3>
                  {arnoldPrograms.map((program) => (
                    <Card
                      key={program.id}
                      className="border-primary/20"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Calendar className="h-5 w-5 text-primary" />
                              <h3 className="font-semibold text-lg">{program.title}</h3>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {t('components.arnoldWorkoutImport.dayProgram', { count: program.days.length })}
                            </p>
                          </div>
                          <Badge variant="outline">
                            <Clock className="h-3 w-3 mr-1" />
                            {new Date(program.timestamp).toLocaleDateString()}
                          </Badge>
                        </div>
                        
                        {expandedProgramId === program.id ? (
                          <div className="space-y-2">
                            <p className="text-sm text-muted-foreground mb-2">{t('components.arnoldWorkoutImport.selectWorkout')}</p>
                            {program.days.map((day, idx) => (
                              <button
                                key={idx}
                                onClick={() => handleSelectWorkoutFromProgram(program, idx)}
                                className="w-full text-left p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors"
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium">{day.day}: {day.workout_name}</span>
                                  <Badge variant="secondary" className="text-xs">
                                    {t('components.arnoldWorkoutImport.exercisesCount', { count: day.exercises.length })}
                                  </Badge>
                                </div>
                              </button>
                            ))}
                            <Button
                              variant="outline"
                              onClick={() => setExpandedProgramId(null)}
                              className="w-full mt-2"
                            >
                              {t('common.cancel')}
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="space-y-2 mb-3">
                              {program.days.slice(0, 3).map((day, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-sm py-1">
                                  <Target className="h-4 w-4 text-muted-foreground" />
                                  <span className="font-medium">{day.day}:</span>
                                  <span className="text-muted-foreground">{day.workout_name}</span>
                                  <Badge variant="secondary" className="text-xs ml-auto">
                                    {t('components.arnoldWorkoutImport.exercisesCount', { count: day.exercises.length })}
                                  </Badge>
                                </div>
                              ))}
                              {program.days.length > 3 && (
                                <p className="text-xs text-muted-foreground text-center pt-1">
                                  {t('components.arnoldWorkoutImport.moreDays', { count: program.days.length - 3 })}
                                </p>
                              )}
                            </div>
                            <Button
                              onClick={() => setExpandedProgramId(program.id)}
                              className="w-full bg-gradient-primary text-black"
                            >
                              {t('components.arnoldWorkoutImport.chooseWorkout')}
                            </Button>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Single Workouts Section */}
              {arnoldWorkouts.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase">{t('components.arnoldWorkoutImport.singleWorkouts')}</h3>
                  {arnoldWorkouts.map((workout) => (
                <Card 
                  key={workout.id} 
                  className="cursor-pointer hover:bg-accent/50 transition-colors border-border/50"
                  onClick={() => handleSelectWorkout(workout)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{workout.title}</h3>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(workout.timestamp).toLocaleDateString()}
                          </div>
                          <div className="flex items-center gap-1">
                            <Bot className="h-3 w-3" />
                            {t('components.arnoldWorkoutImport.arnoldsPlan')}
                          </div>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-xs bg-primary/10 text-primary">
                        AI Generated
                      </Badge>
                    </div>

                    {workout.exercises.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          {workout.exercises.length} exercises planned
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {workout.exercises.slice(0, 3).map((exercise, index) => (
                            <div key={index} className="flex items-center gap-1 text-xs bg-background/50 px-2 py-1 rounded-md">
                              <Target className="h-3 w-3" />
                              {exercise.name}
                              {exercise.sets && exercise.reps && (
                                <span className="text-muted-foreground">
                                  ({exercise.sets}×{exercise.reps})
                                </span>
                              )}
                            </div>
                          ))}
                          {workout.exercises.length > 3 && (
                            <div className="text-xs text-muted-foreground px-2 py-1">
                              {t('components.arnoldWorkoutImport.moreCount', { count: workout.exercises.length - 3 })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        <div className="flex justify-between gap-2 pt-4">
          {arnoldWorkouts.length > 0 && (
            <Button variant="outline" onClick={handleChatWithArnold}>
              <MessageSquare className="h-4 w-4 mr-2" />
              {t('components.arnoldWorkoutImport.getMorePlans')}
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};